/* eslint-disable max-lines */
import type { ComponentProps } from 'react';
import React, { useRef } from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { noop } from 'lodash-es';
import type {
  BackgroundColorProps,
  BackgroundProps,
  BorderRadiusProps,
  HeightProps,
  LeftProps,
  MaxWidthProps,
  PositionProps,
  SizeProps,
  SpaceProps,
  TopProps,
  WidthProps,
  ZIndexProps,
} from 'styled-system';
import {
  background,
  backgroundColor,
  borderRadius,
  color,
  height,
  left,
  maxWidth,
  position,
  size,
  space,
  top,
  width,
  zIndex,
} from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { ArticleImage } from 'common/types/Article';
import { OVERLAY_Z_INDEX } from '../../constants/z-index';
import type { ColorProps } from '../../theme/system-types';
import type {
  BoxSizingProps,
  ThemeHeightProps,
  ThemeRightProps,
  ThemeTopProps,
} from '../../theme/system-utilities';
import { boxSizing, themeHeight, themeRight, themeTop } from '../../theme/system-utilities';
import { breakpoints } from '../../theme/theme';
import type { CarouselItemProps } from '../../types/carousel-item';
import PaginationWithTabMenu from '../CarouselPaginationWithTabMenu';
import { Flex } from '../Flex';
import type { GalleryTabMenuTab } from '../GalleryTabMenu';
import { useCarouselId } from '../image-carousel/utils';
import { Text } from '../Text';
import CloseButton from './CloseButton';
import BigImageHorizontalCarousel from './components/BigImageHorizontalCarousel';
import ThumbnailHorizontalCarousel from './components/ThumbnailCarousel/ThumbnailCarousel';
import { useGalleryActiveSlide } from './hooks/useGalleryActiveSlide';
import { useResizeListener } from './hooks/useGalleryResize';

export interface ImageGalleryOverlayProps {
  activeItem: number;
  items: ArticleImage[];
  thumbnails: CarouselItemProps[];
  closeOverlayHandler?: () => void;
  className?: string;
  name?: string;
  withFixedSize?: boolean;
  ariaLabel?: string;
  productGalleryV2?: boolean;
  galleryTabs?: GalleryTabMenuTab[];
  model3DData?: Nullable<{ path: string }>;
  activeTabId?: string;
  onTabSelect?: (id: string) => void;
}

/* Fix for Mobile Safari on iPhone >=10. Top area on landscape mode is not clickable */
const MOBILE_LANDSCAPE_MEDIA = `
  @media only screen
    and (max-width: ${breakpoints[1]})
    and (max-height: ${breakpoints[0]})
    and (orientation: landscape)
`;

const UnstyledOverlayWrapper = styled.div<
  WidthProps & HeightProps & PositionProps & ZIndexProps & TopProps & LeftProps
>`
  ${width};
  ${height};
  ${position};
  ${zIndex};
  ${top};
  ${left};
`;

const OverlayWrapper = (props: ComponentProps<typeof UnstyledOverlayWrapper>) => (
  <UnstyledOverlayWrapper
    width={1}
    position="fixed"
    zIndex={OVERLAY_Z_INDEX}
    top={0}
    left={0}
    {...props}
  />
);

const UnstyledOverlay = styled(Flex)<
  ColorProps & SizeProps & PositionProps & SpaceProps & BoxSizingProps
>`
  ${color};
  ${size};
  ${position};
  ${space};
  ${boxSizing};
`;

const overlayV2Styles: ComponentProps<typeof UnstyledOverlay> = {
  py: [null, null, 'sp_96'],
  boxSizing: [null, null, 'border-box'],
};

const Overlay = (props: ComponentProps<typeof UnstyledOverlay>) => (
  <UnstyledOverlay
    backgroundColor="sys.neutral.background.default"
    mx="auto"
    size="100%"
    flexDirection="column"
    {...props}
  />
);

const UnstyledTopContainer = styled(Flex)<
  ColorProps &
    BackgroundColorProps &
    BackgroundProps &
    ZIndexProps &
    PositionProps &
    WidthProps &
    HeightProps &
    ThemeTopProps &
    ThemeRightProps &
    BorderRadiusProps
>`
  ${color};
  ${background};
  ${backgroundColor};
  ${zIndex};
  ${position};
  ${width};
  ${height};
  ${themeTop};
  ${themeRight};
  ${borderRadius};

  ${MOBILE_LANDSCAPE_MEDIA} {
    position: fixed;
    pointer-events: none;
    background: transparent;
    padding-top: 16px;

    > * {
      pointer-events: auto;
    }
  }
`;

type TopContainerProps = ComponentProps<typeof UnstyledTopContainer> & {
  productGalleryV2?: boolean;
};

const galleryV2Styles: ComponentProps<typeof UnstyledTopContainer> = {
  position: 'absolute',
  width: 48,
  height: 48,
  themeTop: 'sp_16',
  themeRight: 'sp_16',
  borderRadius: 'circle',
  backgroundColor: 'sys.neutral.background.default',
};

const TopContainer = ({ productGalleryV2, ...props }: TopContainerProps) => (
  <UnstyledTopContainer
    background="rgba(255,255,255, .6)"
    zIndex={1}
    width="100%"
    justifyContent="flex-end"
    alignItems="center"
    {...(productGalleryV2 ? galleryV2Styles : {})}
    {...props}
  />
);

const UnstyledBigImageContainer = styled(Flex)`
  ${MOBILE_LANDSCAPE_MEDIA} {
    margin-top: 12px;
  }
`;

const BigImageContainer = (props: ComponentProps<typeof UnstyledBigImageContainer>) => (
  <UnstyledBigImageContainer
    flex={1}
    width="auto"
    alignItems="center"
    justifyContent="center"
    {...props}
  />
);

const UnstyledBigImageCarouselContainer = styled.div<WidthProps & MaxWidthProps>`
  ${width};
  ${maxWidth};
`;

const BigImageCarouselContainer = (
  props: ComponentProps<typeof UnstyledBigImageCarouselContainer>
) => <UnstyledBigImageCarouselContainer width="100%" maxWidth={100} {...props} />;

const UnstyledThumbnailsContainer = styled(Flex)<
  ThemeHeightProps & BackgroundProps & ZIndexProps & ColorProps & BoxSizingProps
>`
  ${themeHeight};
  ${background};
  ${zIndex};
  ${color};
  ${boxSizing};
`;

const ThumbnailsContainer = (props: ComponentProps<typeof UnstyledThumbnailsContainer>) => (
  <UnstyledThumbnailsContainer
    background="rgba(255,255,255, .6)"
    zIndex={1}
    width="100%"
    themeHeight="sp_128"
    my={['sp_4', 'sp_16']}
    px={['sp_16', null, 0]}
    bg="overlay.white"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    boxSizing={['border-box', null, 'content-box']}
    {...props}
  />
);

const UnstyledImageGalleryOverlayIndicatorText = styled(Text)``;

const ImageGalleryOverlayIndicatorText = (
  props: ComponentProps<typeof UnstyledImageGalleryOverlayIndicatorText>
) => (
  <UnstyledImageGalleryOverlayIndicatorText
    pb={['sp_4', 'sp_16']}
    fontSize={['fs_14', 'fs_16']}
    {...props}
  />
);

const ImageGalleryOverlay = ({
  activeItem: defaultActiveItem,
  thumbnails,
  items,
  closeOverlayHandler: closeOverlay = noop,
  className,
  name,
  withFixedSize,
  ariaLabel,
  productGalleryV2 = false,
  galleryTabs = [],
  model3DData,
  activeTabId,
  onTabSelect,
}: ImageGalleryOverlayProps) => {
  const { r } = useLocalization();
  const baseId = useCarouselId();
  const imageContainer = useRef(null);
  const carouselContainer = useRef(null);
  const { innerHeightViewport } = useResizeListener({
    imageContainer,
    carouselContainer,
    closeOverlay,
  });
  const imageCount = thumbnails.length;
  const [activeItem, setActiveItem] = useGalleryActiveSlide(
    defaultActiveItem,
    imageCount,
    productGalleryV2
  );
  const activeImageIndex = activeItem + 1;
  const viewImageLabel = r.t('h24_view_image', {
    activeImageIndex,
    imageCount,
  });

  return (
    <OverlayWrapper
      height={innerHeightViewport || '100%'}
      data-testid="image-gallery-overlay"
      className={className}
      role="dialog"
      aria-label={ariaLabel}
      aria-modal="true"
    >
      <Overlay {...(productGalleryV2 ? overlayV2Styles : null)}>
        <TopContainer productGalleryV2={productGalleryV2}>
          <CloseButton onClick={closeOverlay} productGalleryV2={productGalleryV2} />
        </TopContainer>
        <BigImageContainer ref={imageContainer}>
          <BigImageCarouselContainer
            data-testid="big-image-carousel-container"
            ref={carouselContainer}
          >
            <BigImageHorizontalCarousel
              activeItem={activeItem}
              items={items}
              updateActiveItem={setActiveItem('product-gallery-main-slide-container')}
              name={name}
              withFixedSize={withFixedSize}
              baseId={baseId}
              productGalleryV2={productGalleryV2}
            />
          </BigImageCarouselContainer>
        </BigImageContainer>
        {productGalleryV2 ? (
          <PaginationWithTabMenu
            currentSlide={activeItem}
            slideCount={items.length}
            tabs={galleryTabs}
            activeTabId={activeTabId}
            model3DData={model3DData}
            onTabSelect={tabId => {
              setActiveItem(null)(0);
              onTabSelect(tabId);
            }}
            tracking={{ isOverlayOpen: true, productGalleryV2: true }}
          />
        ) : (
          <ThumbnailsContainer>
            <ImageGalleryOverlayIndicatorText data-testid="image-gallery-overlay-indicator">
              {`${activeImageIndex} / ${imageCount}`}
            </ImageGalleryOverlayIndicatorText>
            <ThumbnailHorizontalCarousel
              activeItem={activeItem}
              items={thumbnails}
              updateActiveItem={setActiveItem('thumbnail-carousel-container')}
              withFixedSize={withFixedSize}
              ariaLabel={viewImageLabel}
              baseId={baseId}
            />
          </ThumbnailsContainer>
        )}
      </Overlay>
    </OverlayWrapper>
  );
};

export default ImageGalleryOverlay;
